import React, { useEffect, useState } from 'react';
import SubmitButton from '../../../components/SubmitButton';
import Label from '../../../components/Label';
import { axiosKK } from '../../../services/networkRequest';
import {
  AdminOrderRow,
  AdminOrdersResponse,
  AdminOrderAssignmentResponse,
  AvailableCouriersResponse,
  WarehousesResponse,
  OrderAssignmentResponse,
  PartnerUser,
  Warehouse,
  LogisticsDriverOption,
  LogisticsDriversResponse,
} from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import ReactDOM from 'react-dom';
import { Formik } from 'formik';
import IssueModal from '../../../components/admin/IssueModal';
import SplitOrderModal from '../../../components/admin/SplitOrderModal';
import { downloadPdf } from '../../../services/downloadBlob';
import {
  FilterBar,
  DataTable,
  DataTableColumn,
  ExportCsvButton,
  CloseButton,
  AdminNotice,
} from '../../../components/admin/Shared';
import {
  DateRangeDropdown,
  backwardPresets,
} from '../../../components/DateDropdown';
import {
  LockClosedIcon,
  UserPlusIcon,
  FlagIcon,
  RectangleGroupIcon,
  PrinterIcon,
} from '@heroicons/react/24/solid';

const PAGE_SIZE = 15;

function todayISO(): string {
  const d = new Date();
  return d.toISOString().split('T')[0];
}

function getCourierDisplayName(courier: PartnerUser): string {
  if (courier.firstName || courier.lastName) {
    return [courier.firstName, courier.lastName].filter(Boolean).join(' ');
  }
  return courier.name || courier.email || `Courier #${courier.id}`;
}

function getCourierPhone(courier: PartnerUser): string | undefined {
  return courier.phoneNumber || courier.phone;
}

function formatTimeslot(timeslot: string | null): string {
  if (!timeslot) return '—';
  const lower = timeslot.toLowerCase();
  if (lower.includes('morning') || lower.includes('am')) return 'Morning';
  if (lower.includes('afternoon') || lower.includes('pm')) return 'Afternoon';
  const match = timeslot.match(/(\d{1,2}):?\d{0,2}\s*[-–]\s*(\d{1,2})/);
  if (match) {
    const endHour = parseInt(match[2], 10);
    return endHour <= 12 ? 'Morning' : 'Afternoon';
  }
  return timeslot;
}

const ETA_OPTIONS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    ETA_OPTIONS.push(
      `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    );
  }
}

interface OrdersTabProps {
  partnerId: number | null;
}

export default function OrdersTab({ partnerId }: OrdersTabProps) {
  const [orders, setOrders] = useState<AdminOrderRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [networkError, setNetworkError] = useState<string>();
  const [fetching, setFetching] = useState(true);

  const [fromDate, setFromDate] = useState(todayISO);
  const [toDate, setToDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [selectedOrder, setSelectedOrder] = useState<AdminOrderRow | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [issueOrder, setIssueOrder] = useState<AdminOrderRow | null>(null);
  const [splitOrder, setSplitOrder] = useState<AdminOrderRow | null>(null);

  const [drivers, setDrivers] = useState<LogisticsDriverOption[]>([]);
  useEffect(() => {
    if (partnerId == null) {
      setDrivers([]);
      return;
    }
    axiosKK
      .get<LogisticsDriversResponse>('/logistics/dashboard/partner/drivers', {
        params: { partnerId },
      })
      .then(({ data }) => setDrivers(data.drivers || []))
      .catch(() => {
        // Silent — inline picker just becomes unavailable.
      });
  }, [partnerId]);

  const updateDriver = (order: AdminOrderRow, newDriverId: number | null) => {
    const endpoint =
      order.type === 'return' || order.type === 'delivery'
        ? `/logistics/dashboard/partner/returns/${order.id}/driver`
        : `/logistics/dashboard/partner/collections/${order.id}/driver`;
    axiosKK
      .put<AdminOrderRow>(endpoint, { driverId: newDriverId }, {
        params: partnerId != null ? { partnerId } : undefined,
      })
      .then(({ data }) => handleUpdateOrder(data))
      .catch((err) => errorMessage(setNetworkError)(err));
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    setNetworkError(undefined);

    const params: any = { page, pageSize: PAGE_SIZE };
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (partnerId != null) params.partnerId = partnerId;
    if (typeFilter) params.type = typeFilter;

    axiosKK
      .get<AdminOrdersResponse>('/logistics/admin/orders', { params })
      .then(({ data }) => {
        if (cancelled) return;
        setOrders(data.orders);
        setTotalCount(data.totalCount);
      })
      .catch((error) => {
        if (!cancelled) errorMessage(setNetworkError)(error);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });

    return () => { cancelled = true; };
  }, [page, fromDate, toDate, partnerId, typeFilter]);

  const handleFilterChange = (setter: (v: string) => void) => (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    setter(e.target.value);
    setPage(1);
  };

  const handleUpdateOrder = (updated: AdminOrderRow) => {
    setOrders((prev) => prev.map((o) => (o.id === updated.id && o.type === updated.type ? updated : o)));
  };

  const orderColumns: DataTableColumn<AdminOrderRow>[] = [
    {
      key: 'id',
      label: 'ID',
      sticky: true,
      render: (o) => <span className="logistics-order-id">#{o.id}</span>,
    },
    {
      key: 'type',
      label: 'Type',
      render: (o) => (
        <span className={`logistics-type-badge logistics-type-badge--${o.type}`}>
          {o.type}
        </span>
      ),
    },
    { key: 'date', label: 'Date', render: (o) => o.date || '—' },
    { key: 'customer', label: 'Customer', render: (o) => o.customer || '—' },
    {
      key: 'address',
      label: 'Address',
      render: (o) => `${o.address || '—'}, ${o.postcode || ''}`.replace(/, $/, ''),
    },
    { key: 'boxes', label: 'Boxes', render: (o) => o.boxes ?? '—' },
    { key: 'partnerName', label: 'Partner', render: (o) => o.partnerName || '—' },
    {
      key: 'collectionCourier',
      label: 'Collection Courier',
      render: (o) =>
        drivers.length > 0 ? (
          <select
            value={o.collectionDriverId ?? ''}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              updateDriver(o, e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">— Not assigned —</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        ) : (
          o.collectionDriverName || '—'
        ),
    },
    {
      key: 'deliveryCourier',
      label: 'Delivery Courier',
      render: (o) => o.deliveryDriverName || '—',
    },
    { key: 'warehouseName', label: 'Warehouse', render: (o) => o.warehouseName || '—' },
    { key: 'collectionEta', label: 'Collection ETA', render: (o) => o.collectionEta || '—' },
    { key: 'deliveryEta', label: 'Delivery ETA', render: (o) => o.deliveryEta || '—' },
    {
      key: 'timeslot',
      label: 'Requested Timeslot',
      render: (o) => formatTimeslot(o.timeslot),
    },
    {
      key: 'status',
      label: 'Status',
      render: (o) => {
        const items: React.ReactNode[] = [];
        if (o.routeLocked)
          items.push(
            <span key="locked" className="logistics-badge logistics-badge--locked">
              <LockClosedIcon width={14} height={14} /> Locked
            </span>
          );
        if (o.doorstep)
          items.push(
            <span key="door" className="logistics-badge logistics-badge--doorstep">
              Doorstep
            </span>
          );
        if (o.lastScan)
          items.push(
            <span key="scan" className="logistics-badge logistics-badge--scan">
              {o.lastScan}
            </span>
          );
        return items.length > 0 ? <>{items}</> : '—';
      },
    },
  ];

  return (
    <>
      <AdminNotice intent="error">{networkError}</AdminNotice>

      <section className="admin-logistics-orders">
        <FilterBar
          actions={
            <ExportCsvButton
              endpoint="/logistics/admin/orders"
              filename={`logistics-orders-${fromDate || 'all'}.csv`}
              params={{
                fromDate: fromDate || undefined,
                toDate: toDate || undefined,
                partnerId: partnerId ?? undefined,
                type: typeFilter || undefined,
              }}
            />
          }
        >
          <FilterBar.Item label="Date range" htmlFor="orders-date-range">
            <DateRangeDropdown
              id="orders-date-range"
              value={{ start: fromDate || null, end: toDate || null }}
              onChange={({ start, end }) => {
                setPage(1);
                setFromDate(start ?? '');
                setToDate(end ?? '');
              }}
              label="Date range"
              placeholder="All dates"
              presets={backwardPresets()}
            />
          </FilterBar.Item>
          <FilterBar.Item label="Type" htmlFor="orders-type">
            <select
              id="orders-type"
              value={typeFilter}
              onChange={handleFilterChange(setTypeFilter)}
            >
              <option value="">All</option>
              <option value="collection">Collection</option>
              <option value="return">Return</option>
            </select>
          </FilterBar.Item>
        </FilterBar>

        <DataTable<AdminOrderRow>
          columns={orderColumns}
          rows={orders}
          keyOf={(o) => `${o.id}-${o.type}`}
          loading={fetching}
          emptyMessage="No orders found"
          loadingMessage="Loading orders…"
          actionsColumn={{
            label: 'Actions',
            render: (order) => (
              <button
                className="logistics-action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOrder(order);
                  setShowAssignModal(true);
                }}
              >
                <UserPlusIcon width={14} height={14} /> Assign
              </button>
            ),
          }}
        />

        <div className="logistics-pagination">
          <button
            className="logistics-pagination__btn"
            disabled={page <= 1 || fetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            &#8249;
          </button>
          <span className="logistics-pagination__info">
            {fetching && orders.length > 0
              ? 'Loading...'
              : `Page ${page} of ${totalPages || 1}`}
          </span>
          <button
            className="logistics-pagination__btn"
            disabled={page >= totalPages || fetching}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            &#8250;
          </button>
        </div>
      </section>

      {issueOrder && (
        <IssueModal
          show={true}
          orderId={issueOrder.id}
          close={() => setIssueOrder(null)}
        />
      )}

      {splitOrder && (
        <SplitOrderModal
          show={true}
          orderId={splitOrder.id}
          partnerId={splitOrder.partnerId ?? partnerId}
          close={() => setSplitOrder(null)}
        />
      )}

      {selectedOrder && (
        <AssignmentModal
          show={showAssignModal}
          order={selectedOrder}
          close={() => {
            setShowAssignModal(false);
            setSelectedOrder(null);
          }}
          onSuccess={handleUpdateOrder}
          onFlag={() => {
            setIssueOrder(selectedOrder);
            setShowAssignModal(false);
          }}
          onSplit={() => {
            setSplitOrder(selectedOrder);
            setShowAssignModal(false);
          }}
          onDownloadLabels={() =>
            downloadPdf(
              `/logistics/orders/${selectedOrder.id}/labels`,
              `labels-order-${selectedOrder.id}.pdf`
            ).catch((err) => errorMessage(setNetworkError)(err))
          }
        />
      )}
    </>
  );
}

interface AssignmentModalProps {
  show: boolean;
  order: AdminOrderRow;
  close: () => void;
  onSuccess: (updated: AdminOrderRow) => void;
  onFlag: () => void;
  onSplit: () => void;
  onDownloadLabels: () => void;
}

interface AssignmentFormValues {
  collectionCourierId: number | '';
  deliveryCourierId: number | '';
  partnerId: number | '';
  warehouseId: number | '';
  collectionEta: string;
  deliveryEta: string;
  collectionEtaStart: string;
  collectionEtaEnd: string;
  deliveryEtaStart: string;
  deliveryEtaEnd: string;
}

function AssignmentModal({
  show,
  order,
  close,
  onSuccess,
  onFlag,
  onSplit,
  onDownloadLabels,
}: AssignmentModalProps) {
  const [couriers, setCouriers] = useState<PartnerUser[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [networkError, setNetworkError] = useState<string>();
  const [loadingData, setLoadingData] = useState(false);
  const [initialValues, setInitialValues] = useState<AssignmentFormValues>({
    collectionCourierId: '',
    deliveryCourierId: '',
    partnerId: order.partnerId || '',
    warehouseId: '',
    collectionEta: order.collectionEta || '',
    deliveryEta: order.deliveryEta || '',
    collectionEtaStart: order.collectionEtaStart || '',
    collectionEtaEnd: order.collectionEtaEnd || '',
    deliveryEtaStart: order.deliveryEtaStart || '',
    deliveryEtaEnd: order.deliveryEtaEnd || '',
  });

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingData(true);
    setNetworkError(undefined);

    Promise.all([
      axiosKK.get<AvailableCouriersResponse>('/admin/logistics/couriers'),
      axiosKK.get<WarehousesResponse>('/admin/logistics/warehouses'),
      axiosKK.get<OrderAssignmentResponse>(
        `/admin/logistics/orders/${order.id}/assignment`
      ),
    ])
      .then(([couriersRes, warehousesRes, assignmentRes]) => {
        if (cancelled) return;
        setCouriers(couriersRes.data.couriers || []);
        setWarehouses(warehousesRes.data.warehouses || []);

        const assignment = assignmentRes.data;
        const collectionCourier = assignment.collectionCourier ?? assignment.courier;
        const deliveryCourier = assignment.deliveryCourier ?? assignment.courier;

        setInitialValues({
          collectionCourierId: collectionCourier?.id || '',
          deliveryCourierId: deliveryCourier?.id || '',
          partnerId: order.partnerId || '',
          warehouseId: assignment.warehouse?.id || '',
          collectionEta: order.collectionEta || '',
          deliveryEta: order.deliveryEta || '',
          collectionEtaStart: order.collectionEtaStart || '',
          collectionEtaEnd: order.collectionEtaEnd || '',
          deliveryEtaStart: order.deliveryEtaStart || '',
          deliveryEtaEnd: order.deliveryEtaEnd || '',
        });
      })
      .catch((error) => {
        if (!cancelled) errorMessage(setNetworkError)(error);
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });
    return () => { cancelled = true; };
  }, [
    show,
    order.id,
    order.partnerId,
    order.collectionEta,
    order.deliveryEta,
    order.collectionEtaStart,
    order.collectionEtaEnd,
    order.deliveryEtaStart,
    order.deliveryEtaEnd,
  ]);

  useEffect(() => {
    if (!show) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [show, close]);

  const partnerOptions = React.useMemo(() => {
    const map = new Map<number, string>();
    couriers.forEach((c) => {
      if (c.partnerId) {
        const name = c.name || `Partner #${c.partnerId}`;
        if (!map.has(c.partnerId)) map.set(c.partnerId, name);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [couriers]);

  if (!show) return null;

  return ReactDOM.createPortal(
    <div className="logistics-modal__overlay" onClick={close}>
      <div
        className="logistics-modal__container logistics-modal__container--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            setNetworkError(undefined);

            const payload: any = {};
            if (values.collectionCourierId) payload.collectionCourierId = Number(values.collectionCourierId);
            if (values.deliveryCourierId) payload.deliveryCourierId = Number(values.deliveryCourierId);
            if (values.partnerId) payload.partnerId = Number(values.partnerId);
            if (values.warehouseId) payload.warehouseId = Number(values.warehouseId);
            if (values.collectionEta) payload.collectionEta = values.collectionEta;
            if (values.deliveryEta) payload.deliveryEta = values.deliveryEta;
            const windowField = (key: keyof AssignmentFormValues) => {
              const newVal = values[key] as string;
              const oldVal = (initialValues[key] as string) || '';
              if (newVal) payload[key] = newVal;
              else if (oldVal) payload[key] = '';
            };
            windowField('collectionEtaStart');
            windowField('collectionEtaEnd');
            windowField('deliveryEtaStart');
            windowField('deliveryEtaEnd');

            axiosKK
              .put<AdminOrderAssignmentResponse>(
                `/logistics/admin/orders/${order.id}/assignment`,
                payload
              )
              .then(({ data }) => {
                onSuccess(data.order);
                close();
              })
              .catch((error) => {
                errorMessage(setNetworkError)(error);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({ values, isSubmitting, handleChange, handleSubmit, isValid, dirty }) => (
            <form
              onSubmit={handleSubmit}
              className="logistics-modal__form"
              name="logistics-assign-form"
            >
              <div className="logistics-modal__header">
                <div className="logistics-modal__header-info">
                  <h3>Assign Order</h3>
                  <span className="logistics-modal__subtitle">
                    Order #{order.id} &middot; {order.customer}
                  </span>
                </div>
                <CloseButton onClick={close} ariaLabel="Close assignment modal" />
              </div>

              <div className="logistics-modal__secondary-actions">
                <button
                  type="button"
                  className="logistics-action-btn"
                  onClick={onFlag}
                >
                  <FlagIcon width={14} height={14} /> Flag
                </button>
                <button
                  type="button"
                  className="logistics-action-btn"
                  onClick={onSplit}
                >
                  <RectangleGroupIcon width={14} height={14} /> Split
                </button>
                <button
                  type="button"
                  className="logistics-action-btn"
                  onClick={onDownloadLabels}
                >
                  <PrinterIcon width={14} height={14} /> Labels
                </button>
              </div>

              <div className="logistics-modal__body">
                {loadingData ? (
                  <div className="logistics-modal__loading">
                    Loading assignment data...
                  </div>
                ) : (
                  <>
                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">Couriers</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="collectionCourierId">Collection Courier</Label>
                        <select
                          className="logistics-modal__select"
                          id="collectionCourierId"
                          name="collectionCourierId"
                          value={values.collectionCourierId}
                          onChange={handleChange}
                        >
                          <option value="">— Not assigned —</option>
                          {couriers.map((courier) => {
                            const displayName = getCourierDisplayName(courier);
                            const phone = getCourierPhone(courier);
                            return (
                              <option key={courier.id} value={courier.id}>
                                {displayName}
                                {phone ? ` (${phone})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="logistics-modal__field">
                        <Label htmlFor="deliveryCourierId">Delivery Courier</Label>
                        <select
                          className="logistics-modal__select"
                          id="deliveryCourierId"
                          name="deliveryCourierId"
                          value={values.deliveryCourierId}
                          onChange={handleChange}
                        >
                          <option value="">— Not assigned —</option>
                          {couriers.map((courier) => {
                            const displayName = getCourierDisplayName(courier);
                            const phone = getCourierPhone(courier);
                            return (
                              <option key={courier.id} value={courier.id}>
                                {displayName}
                                {phone ? ` (${phone})` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">Partner</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="partnerId">Assign Partner</Label>
                        <select
                          className="logistics-modal__select"
                          id="partnerId"
                          name="partnerId"
                          value={values.partnerId}
                          onChange={handleChange}
                        >
                          <option value="">— Not assigned —</option>
                          {partnerOptions.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">Warehouse</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="warehouseId">Assign Warehouse</Label>
                        <select
                          className="logistics-modal__select"
                          id="warehouseId"
                          name="warehouseId"
                          value={values.warehouseId}
                          onChange={handleChange}
                        >
                          <option value="">— Not assigned —</option>
                          {warehouses.map((wh) => {
                            const location = [wh.city, wh.postcode].filter(Boolean).join(', ');
                            return (
                              <option key={wh.id} value={wh.id}>
                                {wh.name}{location ? ` - ${location}` : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    <div className="logistics-modal__section">
                      <h5 className="logistics-modal__section-title">ETAs</h5>
                      <div className="logistics-modal__field">
                        <Label htmlFor="collectionEta">Collection ETA</Label>
                        <select
                          className="logistics-modal__select"
                          id="collectionEta"
                          name="collectionEta"
                          value={values.collectionEta}
                          onChange={handleChange}
                        >
                          <option value="">— No ETA —</option>
                          {ETA_OPTIONS.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div className="logistics-modal__field logistics-modal__field--inline">
                        <Label>Collection window (override)</Label>
                        <div className="eta-window-row">
                          <select
                            className="logistics-modal__select"
                            name="collectionEtaStart"
                            value={values.collectionEtaStart}
                            onChange={handleChange}
                          >
                            <option value="">— start —</option>
                            {ETA_OPTIONS.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <span className="eta-window-row__sep">→</span>
                          <select
                            className="logistics-modal__select"
                            name="collectionEtaEnd"
                            value={values.collectionEtaEnd}
                            onChange={handleChange}
                          >
                            <option value="">— end —</option>
                            {ETA_OPTIONS.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="logistics-modal__field">
                        <Label htmlFor="deliveryEta">Delivery ETA</Label>
                        <select
                          className="logistics-modal__select"
                          id="deliveryEta"
                          name="deliveryEta"
                          value={values.deliveryEta}
                          onChange={handleChange}
                        >
                          <option value="">— No ETA —</option>
                          {ETA_OPTIONS.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                      <div className="logistics-modal__field logistics-modal__field--inline">
                        <Label>Delivery window (override)</Label>
                        <div className="eta-window-row">
                          <select
                            className="logistics-modal__select"
                            name="deliveryEtaStart"
                            value={values.deliveryEtaStart}
                            onChange={handleChange}
                          >
                            <option value="">— start —</option>
                            {ETA_OPTIONS.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <span className="eta-window-row__sep">→</span>
                          <select
                            className="logistics-modal__select"
                            name="deliveryEtaEnd"
                            value={values.deliveryEtaEnd}
                            onChange={handleChange}
                          >
                            <option value="">— end —</option>
                            {ETA_OPTIONS.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {networkError && <span className="error">{networkError}</span>}
              </div>

              <div className="logistics-modal__footer">
                <SubmitButton
                  disabled={loadingData || !isValid || !dirty}
                  isSubmitting={isSubmitting}
                >
                  Save Assignment
                </SubmitButton>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>,
    document.body
  );
}
